import type { ArticleBlock } from './entities/article';

export const MIN_FIRST_INTERRUPT_PARAGRAPHS = 3;
export const MIN_LEDE_BOUNDARY_PARAGRAPHS = 2;
export const MIN_FIRST_INTERRUPT_CHARS = 450;
export const MIN_PARAGRAPHS_AFTER_RECIRC = 2;
export const MIN_PARAGRAPHS_BETWEEN_ADS = 2;
export const MIN_SUBSTANTIAL_CHARS = 100;
export const MAX_LIST_ITEMS_PER_BLOCK = 5;
export const ITEMS_PER_BODY_DISPLAY = 6;
export const WORDS_PER_BODY_DISPLAY = 450;
export const MAX_BODY_DISPLAY_ADS = 8;
export const SHORT_NEWS_PARAGRAPH_COUNT = 12;
export const SHORT_NEWS_WORD_COUNT = 500;
export const SHORT_ARTICLE_WORD_COUNT = 200;

/** @deprecated Use MIN_FIRST_INTERRUPT_PARAGRAPHS */
export const FIRST_INTERRUPT_PARAGRAPHS = MIN_FIRST_INTERRUPT_PARAGRAPHS;

const MINI_HEADER_TEXTS = ["Don't Miss:", 'Read Next:', 'Read More:', 'Read Now:', 'Also Read:'];
const RECIRC_BLOCK_NAMES = ['acf/read-next', 'acf/taboola-placement'];
const RECIRC_CLASS_SNIPPETS = ['bz-read-next-block', 'read-next-block', 'wp-block-benzinga-read-next'];

export const getBlockPlainText = (block: ArticleBlock | undefined): string =>
  (block?.innerHTML || '').replace(/<[^>]*>/g, '').trim();

const isSocialEmbedQuote = (block: ArticleBlock): boolean => {
  const className = block.tagAttributes?.className || '';
  if (typeof className !== 'string') return false;
  return (
    className.includes('twitter-tweet') || className.includes('instagram-media') || className.includes('reddit-embed')
  );
};

export const isQuoteBlock = (block: ArticleBlock | undefined): boolean => {
  if (!block || isSocialEmbedQuote(block)) return false;
  if (block.blockName === 'core/quote') return true;
  return block.tag === 'blockquote';
};

export const isCountableBodyParagraph = (block: ArticleBlock | undefined): boolean => {
  if (!block || !block.innerHTML) return false;
  const isParagraph = block.tag === 'p' || block.blockName === 'core/paragraph' || isQuoteBlock(block);
  if (!isParagraph) return false;

  const plainText = getBlockPlainText(block);
  if (!plainText) return false;
  if (MINI_HEADER_TEXTS.some(header => plainText.startsWith(header))) return false;

  const lowerText = plainText.toLowerCase();
  if (
    plainText.length < 50 &&
    (lowerText.startsWith('image via') ||
      lowerText.startsWith('photo via') ||
      lowerText.startsWith('image:') ||
      lowerText.startsWith('photo:') ||
      lowerText.includes('image via shutterstock') ||
      lowerText.includes('photo: shutterstock') ||
      lowerText.includes('image: shutterstock') ||
      lowerText.includes('image via getty') ||
      lowerText.includes('photo: getty') ||
      lowerText.includes('image: getty') ||
      lowerText.match(/^(photo|image)\s*(via|:)\s*/i))
  ) {
    return false;
  }

  return true;
};

export const isSubstantialParagraph = (block: ArticleBlock | undefined): boolean =>
  isCountableBodyParagraph(block) && getBlockPlainText(block).length >= MIN_SUBSTANTIAL_CHARS;

export const isRecircBlock = (block: ArticleBlock | undefined): boolean => {
  if (!block) return false;
  if (block.blockName && RECIRC_BLOCK_NAMES.includes(block.blockName)) return true;

  const className = block.tagAttributes?.className || '';
  if (typeof className === 'string' && RECIRC_CLASS_SNIPPETS.some(snippet => className.includes(snippet))) {
    return true;
  }

  const html = (block.innerHTML || '').toLowerCase();
  return (
    html.includes('read next:') ||
    html.includes('read more:') ||
    html.includes("don't miss:") ||
    html.includes('also read:')
  );
};

const looksLikeDateline = (plainText: string): boolean =>
  /\b(19|20)\d{2}\b/.test(plainText) || /chainwire|newswire|globe.?news|press\s*release/i.test(plainText);

export const isPseudoHeadingBlock = (block: ArticleBlock | undefined): boolean => {
  if (!block) return false;
  const isParagraph = block.tag === 'p' || block.blockName === 'core/paragraph';
  if (!isParagraph) return false;

  const plainText = getBlockPlainText(block);
  if (!plainText || plainText.length >= MIN_SUBSTANTIAL_CHARS || looksLikeDateline(plainText)) return false;

  const html = (block.innerHTML || '').trim();
  const strongOnly = /^(<p[^>]*>)?\s*(<span[^>]*>)?\s*<strong\b[\s\S]*<\/strong>\s*(<\/span>)?\s*(<\/p>)?$/i.test(html);
  return strongOnly || plainText.endsWith(':');
};

export const isHeadingBlock = (block: ArticleBlock | undefined): boolean => {
  if (!block) return false;
  if (block.blockName === 'core/heading') return true;
  if (block.tag && /^h[1-6]$/i.test(block.tag)) return true;
  const className = block.tagAttributes?.className || '';
  if (typeof className === 'string' && className.includes('wp-block-heading')) return true;
  if (/^<h[1-6]\b/i.test((block.innerHTML || '').trim())) return true;
  return isPseudoHeadingBlock(block);
};

export const isFigureBlock = (block: ArticleBlock | undefined): boolean => {
  if (!block) return false;
  if (block.blockName === 'core/image' || block.tag === 'figure' || block.tag === 'img') return true;
  const className = block.tagAttributes?.className || '';
  return (
    typeof className === 'string' && (className.includes('wp-block-image') || className.includes('wp-block-embed'))
  );
};

export const isListBlock = (block: ArticleBlock | undefined): boolean => {
  if (!block) return false;
  if (block.blockName === 'core/list') return true;
  return block.tag === 'ul' || block.tag === 'ol';
};

export const isSubstantialList = (block: ArticleBlock | undefined): boolean =>
  isListBlock(block) && getBlockPlainText(block).length >= MIN_SUBSTANTIAL_CHARS;

export const countListItems = (block: ArticleBlock | undefined): number =>
  (block?.innerHTML || '').match(/<li\b/gi)?.length ?? 0;

export const countPlacementUnits = (blocks: ArticleBlock[]): number => {
  if (!Array.isArray(blocks)) return 0;
  return blocks.reduce((count, block) => {
    if (isCountableBodyParagraph(block)) return count + 1;
    if (isListBlock(block)) return count + countListItems(block);
    return count;
  }, 0);
};

const isContentUnit = (block: ArticleBlock | undefined): boolean =>
  isSubstantialParagraph(block) || isSubstantialList(block);

const isLeadgenSlot = (block: ArticleBlock | undefined): boolean =>
  !!block && (block.tagAttributes?.id === 'in-content-slot' || (block.blockName || '').includes('in-content'));

const isFirstHeadingIndex = (blocks: ArticleBlock[], index: number): boolean =>
  isHeadingBlock(blocks[index]) && !blocks.slice(0, index).some(block => isHeadingBlock(block));

const isFirstListIndex = (blocks: ArticleBlock[], index: number): boolean =>
  isListBlock(blocks[index]) && !blocks.slice(0, index).some(block => isListBlock(block));

const isSectionCompleteIndex = (blocks: ArticleBlock[], index: number): boolean => {
  if (index <= 0 || index >= blocks.length) return false;
  return isHeadingBlock(blocks[index]) && isListBlock(blocks[index - 1]);
};

const isListStackIndex = (blocks: ArticleBlock[], index: number): boolean =>
  index > 0 && isListBlock(blocks[index]) && isSubstantialList(blocks[index - 1]);

const LIST_SPLIT_GROUP = 'listSplitGroup';
const LIST_SPLIT_ATTR = 'data-list-split-group';

export const getTopLevelListItems = (html: string): string[] => {
  const items: string[] = [];
  const tagRe = /<\/?li\b[^>]*>/gi;
  let listItemDepth = 0;
  let itemStart = -1;
  let match: RegExpExecArray | null;
  while ((match = tagRe.exec(html))) {
    const isClose = match[0][1] === '/';
    if (!isClose) {
      if (listItemDepth === 0) itemStart = match.index;
      listItemDepth += 1;
      continue;
    }
    listItemDepth = Math.max(0, listItemDepth - 1);
    if (listItemDepth === 0 && itemStart !== -1) {
      items.push(html.slice(itemStart, match.index + match[0].length));
      itemStart = -1;
    }
  }
  return items;
};

const listTagFromHtml = (block: ArticleBlock, html: string): 'ul' | 'ol' =>
  block.tag === 'ol' || /^<ol\b/i.test(html) ? 'ol' : 'ul';

const listOpenTag = (html: string, tag: 'ul' | 'ol', start?: number, groupId?: string): string => {
  const existing = html.match(/^<(ul|ol)\b[^>]*>/i)?.[0] || `<${tag}>`;
  const withoutMeta = existing
    .replace(/\sstart="[^"]*"/i, '')
    .replace(new RegExp(`\\s${LIST_SPLIT_ATTR}="[^"]*"`, 'i'), '');
  const withStart =
    tag === 'ol' && start && start > 1 ? withoutMeta.replace(/^<ol\b/i, `<ol start="${start}"`) : withoutMeta;
  if (!groupId) return withStart;
  return withStart.replace(/^<(ul|ol)\b/i, `<$1 ${LIST_SPLIT_ATTR}="${groupId}"`);
};

const splitGroupOf = (block: ArticleBlock | undefined): string | undefined => {
  if (!block) return undefined;
  const fromAttrs = block.attrs && typeof block.attrs === 'object' ? block.attrs[LIST_SPLIT_GROUP] : undefined;
  if (typeof fromAttrs === 'string' && fromAttrs) return fromAttrs;
  return (block.innerHTML || '').match(new RegExp(`${LIST_SPLIT_ATTR}="([^"]+)"`, 'i'))?.[1];
};

const stripSplitMeta = (block: ArticleBlock): ArticleBlock => {
  const html = block.innerHTML || '';
  if (!html.includes(LIST_SPLIT_ATTR) && !splitGroupOf(block)) return block;
  const attrs = { ...(block.attrs || {}) };
  delete attrs[LIST_SPLIT_GROUP];
  return {
    ...block,
    attrs,
    innerHTML: html.replace(new RegExp(`\\s${LIST_SPLIT_ATTR}="[^"]*"`, 'gi'), ''),
  };
};

const mergeListBlocks = (first: ArticleBlock, second: ArticleBlock): ArticleBlock => {
  const html = first.innerHTML || '';
  const tag = listTagFromHtml(first, html);
  const items = [...getTopLevelListItems(html), ...getTopLevelListItems(second.innerHTML || '')];
  const existingStart = Number(html.match(/\sstart="(\d+)"/i)?.[1] || 1);
  const groupId = splitGroupOf(first);
  return {
    ...first,
    attrs: { ...(first.attrs || {}), ...(groupId ? { [LIST_SPLIT_GROUP]: groupId } : {}) },
    innerHTML: `${listOpenTag(html, tag, existingStart, groupId)}${items.join('')}</${tag}>`,
    tag,
  };
};

export const mergeUnbrokenListSplits = (blocks: ArticleBlock[]): ArticleBlock[] => {
  const merged: ArticleBlock[] = [];
  for (const block of blocks) {
    const previous = merged[merged.length - 1];
    const group = splitGroupOf(block);
    if (group && group === splitGroupOf(previous) && isListBlock(previous) && isListBlock(block)) {
      merged[merged.length - 1] = mergeListBlocks(previous, block);
      continue;
    }
    merged.push(block);
  }

  const remainingGroups = new Map<string, number>();
  for (const block of merged) {
    const group = splitGroupOf(block);
    if (group) remainingGroups.set(group, (remainingGroups.get(group) || 0) + 1);
  }

  return merged.map(block => {
    const group = splitGroupOf(block);
    if (!group || remainingGroups.get(group) === 1) return stripSplitMeta(block);
    return block;
  });
};

export const splitOversizedLists = (blocks: ArticleBlock[]): ArticleBlock[] => {
  if (!Array.isArray(blocks)) return [];

  return blocks.flatMap((block, blockIndex) => {
    if (!isListBlock(block)) return [block];

    const html = block.innerHTML || '';
    const items = getTopLevelListItems(html);
    if (items.length <= MAX_LIST_ITEMS_PER_BLOCK) return [block];

    const tag = listTagFromHtml(block, html);
    const existingStart = Number(html.match(/\sstart="(\d+)"/i)?.[1] || 1);
    const groupId = splitGroupOf(block) || `list-split-${blockIndex}`;

    const chunks: ArticleBlock[] = [];
    for (let offset = 0; offset < items.length; offset += MAX_LIST_ITEMS_PER_BLOCK) {
      const chunkStart = existingStart + offset;
      chunks.push({
        ...block,
        attrs: { ...(block.attrs || {}), [LIST_SPLIT_GROUP]: groupId },
        innerHTML: `${listOpenTag(html, tag, chunkStart, groupId)}${items
          .slice(offset, offset + MAX_LIST_ITEMS_PER_BLOCK)
          .join('')}</${tag}>`,
        tag,
      });
    }
    return chunks;
  });
};

export const prepareBlocksForInterrupts = (blocks: ArticleBlock[]): ArticleBlock[] =>
  splitOversizedLists(relocateRecircAfterFirstInterrupt(blocks));

const substantialParagraphsBefore = (blocks: ArticleBlock[], index: number): number => {
  let counted = 0;
  for (let cursor = index - 1; cursor >= 0; cursor--) {
    if (isHeadingBlock(blocks[cursor])) break;
    if (isSubstantialParagraph(blocks[cursor])) counted += 1;
  }
  return counted;
};

export const isSafeInterruptIndex = (blocks: ArticleBlock[], index: number): boolean => {
  if (index <= 0 || index >= blocks.length) return false;

  const target = blocks[index];
  const previous = blocks[index - 1];
  if (!target || !previous) return false;
  if (isHeadingBlock(target) || isFigureBlock(target) || isRecircBlock(target) || isListBlock(target)) return false;
  if (isLeadgenSlot(target) || isLeadgenSlot(previous)) return false;
  if (isHeadingBlock(previous) || isFigureBlock(previous) || isRecircBlock(previous)) return false;
  if (!isCountableBodyParagraph(target)) return false;
  if (!isSubstantialParagraph(previous) && !isListBlock(previous)) return false;
  if (substantialParagraphsBefore(blocks, index) < 1) return false;

  return true;
};

export const findSafeInsertIndex = (blocks: ArticleBlock[], startIndex: number): number => {
  for (let index = Math.max(1, startIndex); index < blocks.length; index++) {
    if (isSafeInterruptIndex(blocks, index)) return index;
  }
  return -1;
};

const isLedeBoundaryIndex = (blocks: ArticleBlock[], index: number): boolean =>
  isFigureBlock(blocks[index]) || isFirstHeadingIndex(blocks, index) || isFirstListIndex(blocks, index);

const hasLedeStructureBefore = (blocks: ArticleBlock[], index: number): boolean =>
  blocks.slice(0, index).some(block => isFigureBlock(block) || isHeadingBlock(block) || isListBlock(block));

export const findFirstBreakIndex = (blocks: ArticleBlock[]): number => {
  if (!Array.isArray(blocks) || blocks.length < 2) return -1;

  let substantialCount = 0;
  for (let index = 0; index < blocks.length; index++) {
    if (isSubstantialParagraph(blocks[index])) substantialCount += 1;

    const insertIndex = index + 1;
    if (insertIndex >= blocks.length) break;

    const atLedeBoundary = isLedeBoundaryIndex(blocks, insertIndex);
    const minNeeded =
      atLedeBoundary || hasLedeStructureBefore(blocks, insertIndex)
        ? MIN_LEDE_BOUNDARY_PARAGRAPHS
        : MIN_FIRST_INTERRUPT_PARAGRAPHS;
    if (substantialCount < minNeeded) continue;
    if (atLedeBoundary || isSafeInterruptIndex(blocks, insertIndex)) return insertIndex;
  }

  return -1;
};

export const findBreakIndices = (blocks: ArticleBlock[]): number[] => {
  if (!Array.isArray(blocks) || blocks.length < 3) return [];

  const firstBreakIndex = findFirstBreakIndex(blocks);
  if (firstBreakIndex === -1) return [];

  const breaks = [firstBreakIndex];
  let substantialSinceBreak = 0;
  for (let index = firstBreakIndex; index < blocks.length; index++) {
    if (isContentUnit(blocks[index])) substantialSinceBreak += 1;

    const insertIndex = index + 1;
    const minSinceBreak = isListStackIndex(blocks, insertIndex) ? 1 : MIN_PARAGRAPHS_BETWEEN_ADS;
    if (substantialSinceBreak < minSinceBreak) continue;
    if (
      !isSafeInterruptIndex(blocks, insertIndex) &&
      !isSectionCompleteIndex(blocks, insertIndex) &&
      !isListStackIndex(blocks, insertIndex)
    ) {
      continue;
    }
    if (breaks.includes(insertIndex)) continue;

    breaks.push(insertIndex);
    substantialSinceBreak = 0;
  }

  return breaks;
};

export const selectBreakIndices = (breaks: number[], count: number): number[] => {
  if (count <= 0 || !breaks.length) return [];
  if (count >= breaks.length) return breaks;
  if (count === 1) return [breaks[0]];

  const picked: number[] = [];
  for (let slot = 0; slot < count; slot++) {
    const index = Math.round((slot * (breaks.length - 1)) / (count - 1));
    const breakIndex = breaks[index];
    if (!picked.includes(breakIndex)) picked.push(breakIndex);
  }
  return picked;
};

export const findFirstInterruptIndex = (blocks: ArticleBlock[] | undefined): number => {
  if (!Array.isArray(blocks) || blocks.length === 0) return -1;
  return findBreakIndices(blocks)[0] ?? -1;
};

const recircNeedsMove = (blocks: ArticleBlock[], recircIndex: number, firstBreakIndex: number): boolean => {
  if (recircIndex <= firstBreakIndex) return true;

  let substantialBetween = 0;
  for (let index = firstBreakIndex; index < recircIndex; index++) {
    if (isSubstantialParagraph(blocks[index])) substantialBetween += 1;
  }
  if (substantialBetween < MIN_PARAGRAPHS_AFTER_RECIRC) return true;

  const next = blocks[recircIndex + 1];
  return isHeadingBlock(next) && substantialParagraphsBefore(blocks, recircIndex) < 1;
};

export const relocateRecircAfterFirstInterrupt = (blocks: ArticleBlock[]): ArticleBlock[] => {
  const recircIndex = blocks.findIndex(block => isRecircBlock(block));
  if (recircIndex === -1) return blocks;

  const recirc = blocks[recircIndex];
  const withoutRecirc = blocks.filter((_, index) => index !== recircIndex);
  const firstBreakIndex = findFirstInterruptIndex(withoutRecirc);
  if (firstBreakIndex === -1) return blocks;
  if (!recircNeedsMove(blocks, recircIndex, firstBreakIndex)) return blocks;

  const afterCopy = findIndexAfterCountableParagraphs(withoutRecirc, firstBreakIndex, MIN_PARAGRAPHS_AFTER_RECIRC);
  const safeIndex = findSafeInsertIndex(withoutRecirc, afterCopy === -1 ? firstBreakIndex : afterCopy);
  const insertIndex = safeIndex === -1 ? withoutRecirc.length : safeIndex;
  if (insertIndex === recircIndex) return blocks;

  withoutRecirc.splice(insertIndex, 0, recirc);
  return withoutRecirc;
};

export const findIndexAfterCountableParagraphs = (
  blocks: ArticleBlock[],
  startIndex: number,
  paragraphCount: number,
): number => {
  let counted = 0;
  for (let index = Math.max(0, startIndex); index < blocks.length; index++) {
    if (!isSubstantialParagraph(blocks[index])) continue;
    counted += 1;
    if (counted >= paragraphCount) return index + 1;
  }
  return -1;
};

export const findLeadgenInsertIndex = (blocks: ArticleBlock[]): number => {
  if (!blocks.length) return 0;

  const breaks = findBreakIndices(blocks);
  const firstBreakIndex = breaks[0] ?? -1;
  const recircIndex = blocks.findIndex(block => isRecircBlock(block));

  const isUsableLeadgenIndex = (index: number): boolean =>
    index > firstBreakIndex + 1 &&
    (isSafeInterruptIndex(blocks, index) ||
      isSectionCompleteIndex(blocks, index) ||
      isListStackIndex(blocks, index) ||
      index === blocks.length);

  if (recircIndex !== -1) {
    const afterRecirc = breaks.find(breakIndex => breakIndex > recircIndex && isUsableLeadgenIndex(breakIndex));
    if (afterRecirc !== undefined) return afterRecirc;

    const parkedIndex = findSafeInsertIndex(blocks, recircIndex + 1);
    if (parkedIndex !== -1 && isUsableLeadgenIndex(parkedIndex)) return parkedIndex;
  }

  const laterBreak = breaks.find(breakIndex => isUsableLeadgenIndex(breakIndex));
  if (laterBreak !== undefined) return laterBreak;

  return blocks.length;
};

export const suggestedBodyAdCount = (wordCount: number | null | undefined, paragraphCount: number): number => {
  const words = typeof wordCount === 'number' ? wordCount : paragraphCount * 40;
  if (words < SHORT_ARTICLE_WORD_COUNT || paragraphCount < 3) return 0;
  if (words < SHORT_NEWS_WORD_COUNT || paragraphCount < SHORT_NEWS_PARAGRAPH_COUNT) return 2;
  const fromWords = Math.round(words / WORDS_PER_BODY_DISPLAY);
  const fromUnits = Math.floor(paragraphCount / ITEMS_PER_BODY_DISPLAY);
  return Math.min(MAX_BODY_DISPLAY_ADS, Math.max(3, fromWords, fromUnits));
};

export const bodyAdCountForBreaks = (suggestedCount: number, breakCount: number, displayOnly: boolean): number => {
  if (suggestedCount <= 0 || breakCount <= 0) return 0;
  if (displayOnly) return Math.min(suggestedCount, breakCount);
  return Math.min(MAX_BODY_DISPLAY_ADS, suggestedCount, breakCount);
};

export const isShortNewsLength = (wordCount: number | null | undefined, paragraphCount: number): boolean => {
  if (paragraphCount < SHORT_NEWS_PARAGRAPH_COUNT) return true;
  return typeof wordCount === 'number' && wordCount < SHORT_NEWS_WORD_COUNT;
};
